import React, { useState } from 'react';
import { Share, FileText, Presentation, Download, ExternalLink, Copy, Check } from 'lucide-react';
import { exportToPDF } from '../utils/pdfExport';
import '../styles/ShareMenu.css';

interface ShareMenuProps {
  onStartPresentation: () => void;
}

const ShareMenu: React.FC<ShareMenuProps> = ({ onStartPresentation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handlePDFExport = async () => {
    setIsExporting(true);
    try {
      const success = await exportToPDF('waymining-portfolio');
      if (success) {
        console.log('PDF exported successfully');
      }
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePresentationShare = () => {
    const url = `${window.location.origin}${window.location.pathname}?presentation=true`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="share-menu">
      <button 
        className={`share-trigger ${isOpen ? 'active' : ''}`}
        onClick={toggleMenu}
        title="Share Options"
      >
        <Share size={20} />
        <span>Share</span>
      </button>

      {isOpen && (
        <div className="share-dropdown">
          <div className="share-header">
            <h3>Share Waymining FMS</h3>
            <p>Choose how to share this beautiful site</p>
          </div>

          <div className="share-options">
            {/* PDF Export Option */}
            <div className="share-option pdf-option">
              <div className="option-icon">
                <FileText size={24} />
              </div>
              <div className="option-content">
                <h4>Export as PDF</h4>
                <p>Download a beautiful PDF document for proposals, sharing, or offline viewing</p>
                <button 
                  className="option-button pdf-btn"
                  onClick={handlePDFExport}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <>
                      <div className="spinner"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Download PDF
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Presentation Mode Option */}
            <div className="share-option presentation-option">
              <div className="option-icon">
                <Presentation size={24} />
              </div>
              <div className="option-content">
                <h4>Presentation Mode</h4>
                <p>Professional slide presentation for demos, meetings, and live presentations</p>
                <div className="presentation-buttons">
                  <button 
                    className="option-button present-btn"
                    onClick={onStartPresentation}
                  >
                    <Presentation size={16} />
                    Start Presentation
                  </button>
                  <button 
                    className="option-button share-link-btn"
                    onClick={handlePresentationShare}
                  >
                    {copied ? (
                      <>
                        <Check size={16} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Direct Website Link */}
            <div className="share-option website-option">
              <div className="option-icon">
                <ExternalLink size={24} />
              </div>
              <div className="option-content">
                <h4>Share Website</h4>
                <p>Send the live website link for interactive browsing</p>
                <button 
                  className="option-button website-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? (
                    <>
                      <Check size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy Website Link
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="share-footer">
            <p>💡 <strong>Tip:</strong> PDF for documents, Presentation for meetings, Website for interactive exploration</p>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && <div className="share-backdrop" onClick={() => setIsOpen(false)} />}
    </div>
  );
};

export default ShareMenu;